import { useEffect, useState } from "react";
import Select from "react-select";
import { useSelector } from "react-redux";
import { isBefore } from "date-fns";
import { useNavigate } from "react-router-dom";
import { Notification, useToaster } from "rsuite";
import { FormProvider, useForm } from "react-hook-form";
/* API Methods */
import { putComplianceOverride } from "pages/officeMonitoring/services/api";
/* Utilities */
import { IconOption, IconSingleValue } from "./utils";

const eventTypes = [
  {
    value: "Authorized WFH",
  },
  {
    value: "Pending Leave Filing",
  },
  {
    value: "Official Business",
  },
  {
    value: "Invalidate",
  },
];

export const EditComplianceOverrideModal = ({ author, renderData }) => {
  const [formData, setFormData] = useState({
    employee: "",
    startDate: "",
    endDate: "",
    createdDate: "",
    updatedDate: "",
    status: "",
    remarks: "",
    type: null,
    author: null,
  });
  const overrideFormData = useSelector((state) => state.overrideFormData.value);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    values: formData,
    resetOptions: {
      keepDirtyValues: true, // keep dirty fields unchanged, but update defaultValues
    },
  });
  const navigate = useNavigate();
  const toaster = useToaster();

  const [selectedType, setSelectedType] = useState(null);

  const [isLoading, setIsLoading] = useState(false);

  const onSubmit = handleSubmit((data) => {
    setIsLoading(true);
    const modalElement = document.getElementById("modal-edit-override");
    const modal = window.bootstrap.Modal.getInstance(modalElement);

    const offCanvasElement = document.getElementById("offcanvasEnd");
    const offcanvas = window.bootstrap.Offcanvas.getInstance(offCanvasElement);
    putComplianceOverride(formData.id, {
      complianceOverrideId: formData.id,
      employee: data.employee,
      startDate: data.startDate,
      endDate: data.endDate,
      createdDate: data.createdDate,
      updatedDate: data.updatedDate,
      status: data.status,
      remarks: data.remarks,
      type: data.type,
      author: author,
    })
      .then(() => {
        toaster.push(
          <Notification type="success" header="Submitted Successfully" closable>
            Your office attendance override has been updated
          </Notification>,
          {
            placement: "topEnd",
            duration: 5000,
          }
        );
        renderData();
      })
      .catch((error) => {
        if (error.response) {
          // The request was made and the server responded with a status code
          // that falls out of the range of 2xx

          switch (error.response.status) {
            case 401: {
              toaster.push(
                <Notification type="error" header="Unauthorized" closable>
                  The operation failed. You don't have permission to perform
                  this action.
                </Notification>,
                {
                  placement: "topEnd",
                  duration: 5000,
                }
              );
              break;
            }
            case 400: {
              console.log(error.response);
              toaster.push(
                <Notification type="error" header="Bad Request" closable>
                  {error.response.data}
                </Notification>,
                {
                  placement: "topEnd",
                  duration: 5000,
                }
              );
              break;
            }
            default:
              navigate(`/${error.response.status}`);
          }
        } else {
          toaster.push(
            <Notification type="error" header="Something went wrong" closable>
              The operation failed. Please try again later or contact the
              administrator of this page.
            </Notification>,
            {
              placement: "topEnd",
              duration: 5000,
            }
          );
        }
      })
      .finally(() => {
        setIsLoading(false);
        setSelectedType(null);
        modal.hide();
        offcanvas.hide();
      });
  });

  useEffect(() => {
    setFormData(overrideFormData);
    setSelectedType({ value: overrideFormData.type });
  }, [overrideFormData]);

  return (
    <div
      className="modal modal-blur fade"
      id="modal-edit-override"
      tabIndex="-1"
      role="dialog"
      aria-modal="true"
      data-bs-backdrop="static"
      data-bs-keyboard="false"
    >
      <div
        className="modal-dialog modal-md modal-dialog-centered"
        role="document"
      >
        <div className="modal-content">
          {isLoading && (
            <div className="progress">
              <div className="progress-bar progress-bar-indeterminate bg-primary"></div>
            </div>
          )}
          <div className="modal-header">
            <h5 className="modal-title">Edit Office Attendance Override</h5>
            <button
              type="button"
              className="btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
            ></button>
          </div>
          <div className="modal-body">
            <FormProvider>
              <form onSubmit={(e) => e.preventDefault()}>
                <div className="row">
                  <div className="col-lg-12">
                    <div className="mb-3">
                      <label className="form-label required">
                        Override Type
                      </label>
                      <Select
                        {...register("type", { required: true })}
                        options={eventTypes}
                        isSearchable={true}
                        components={{
                          Option: IconOption,
                          SingleValue: IconSingleValue,
                        }}
                        value={selectedType}
                        onChange={(e) => {
                          setSelectedType(e);
                          setFormData({
                            ...formData,
                            type: e.value,
                          });
                        }}
                        className={errors.type ? "is-invalid" : ""}
                      />
                      {errors.type?.type === "required" && (
                        <div className="invalid-feedback">
                          This field is required
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-lg-12">
                    <div className="mb-3">
                      <label className="form-label required">Who</label>
                      {/* <Multiselect
                    options={formData.users} // Options to display in the dropdown
                    selectedValues={formData.users} // Preselected value to persist in dropdown
                    onSelect={() => {}} // Function will trigger on select event
                    onRemove={() => {}} // Function will trigger on remove event
                    displayValue="email" // Property name to display in the dropdown options
                    isObject={false}
                    placeholder="Email"
                  /> */}
                      <input
                        {...register("employee")}
                        type="text"
                        className={
                          errors.employee
                            ? "form-control is-invalid"
                            : "form-control"
                        }
                        name="example-text-input"
                        placeholder="Employee Name"
                        value={formData.employee || ""}
                        disabled={true}
                      />
                      {errors.type?.type === "required" && (
                        <div className="invalid-feedback">
                          This field is required
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-lg-6">
                    <div className="mb-3">
                      <label className="form-label required">Start Date</label>
                      <input
                        {...register("startDate", { required: true })}
                        type="date"
                        className={
                          errors.startDate
                            ? "form-control is-invalid"
                            : "form-control"
                        }
                        value={formData.startDate}
                        onChange={(e) => {
                          if (
                            isBefore(
                              new Date(formData.endDate),
                              new Date(e.target.value)
                            )
                          ) {
                            setFormData({
                              ...formData,
                              startDate: e.target.value,
                              endDate: e.target.value,
                            });
                          } else {
                            setFormData({
                              ...formData,
                              startDate: e.target.value,
                            });
                          }
                        }}
                      />
                      {errors.startDate?.type === "required" && (
                        <div className="invalid-feedback">
                          This field is required
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-lg-6">
                    <div className="mb-3">
                      <label className="form-label required">End Date</label>
                      <input
                        {...register("endDate", { required: true })}
                        type="date"
                        className={
                          errors.endDate
                            ? "form-control is-invalid"
                            : "form-control"
                        }
                        value={formData.endDate}
                        onChange={(e) => {
                          if (
                            isBefore(
                              new Date(e.target.value),
                              new Date(formData.startDate)
                            )
                          ) {
                            alert("Please select a valid end date.");
                          } else {
                            setFormData({
                              ...formData,
                              endDate: e.target.value,
                            });
                          }
                        }}
                      />
                      {errors.endDate?.type === "required" && (
                        <div className="invalid-feedback">
                          This field is required
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-lg-12">
                    <div>
                      <label className="form-label">
                        Remarks{" "}
                        <span className="small text-muted">(Optional)</span>
                      </label>
                      <textarea
                        {...register("remarks", {
                          required: true,
                          maxLength: 300,
                        })}
                        className={
                          errors.remarks
                            ? "form-control is-invalid"
                            : "form-control"
                        }
                        rows="3"
                        value={formData.remarks}
                        onChange={(e) =>
                          setFormData({ ...formData, remarks: e.target.value })
                        }
                      ></textarea>
                      {errors.remarks?.type === "maxLength" && (
                        <div className="invalid-feedback">
                          Max allowed characters (100) reached
                        </div>
                      )}
                      {errors.remarks?.type === "required" && (
                        <div className="invalid-feedback">
                          This field is required
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </form>
            </FormProvider>
          </div>

          <div className="modal-footer">
            <button
              className="btn btn-link link-secondary"
              data-bs-dismiss="modal"
            >
              Cancel
            </button>
            <button
              className="btn btn-primary "
              onClick={onSubmit}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  Loading <span className="animated-dots"></span>
                </>
              ) : (
                "Save Changes"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
